import React, { useState } from 'react';
import axios from 'axios';

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `₹${(n/10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

function MarginRequestCard({ collateralAvailable, isMobile }) {
  const [step,    setStep]    = useState('idle');
  const [loading, setLoading] = useState(false);

  const handleRequest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/me/request-margin-increase', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStep('done');
    } catch {
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding: isMobile ? '14px' : '12px 14px', marginBottom: isMobile ? 10 : 0 }}>
      <div style={{ fontSize:10, color:'#888', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>
        Collateral Increase Request
      </div>

      {step === 'idle' && (
        <>
          <div style={{ fontSize:12, color:'#555', marginBottom:10, lineHeight:1.5 }}>
            You may request an increase to your pledged collateral to optimize your trading margin.
          </div>

          {/* Available amount — always shown */}
          <div style={{ background:'#fdf5f7', border:'1px solid #e8c5cc', borderRadius:8, padding:'8px 12px', marginBottom:12 }}>
            <div style={{ fontSize:10, color:'#888', marginBottom:2, textTransform:'uppercase', letterSpacing:'0.06em' }}>Available to Pledge</div>
            <div style={{ fontSize:22, fontWeight:700, color: collateralAvailable > 0 ? '#6B1C2A' : '#bbb' }}>
              {collateralAvailable > 0 ? fmt(collateralAvailable) : '₹0'}
            </div>
          </div>

          {/* Button always enabled */}
          <button onClick={() => setStep('confirm')}
            style={{ padding:'7px 16px', background:'#6B1C2A', color:'#fff', border:'none', borderRadius:7, fontSize:12, fontWeight:600, cursor:'pointer' }}>
            Request Margin Increase
          </button>
        </>
      )}

      {step === 'confirm' && (
        <>
          <div style={{ fontSize:13, color:'#333', marginBottom:12, lineHeight:1.5 }}>
            Are you sure you want to request an increase in your collateral pledge? Our team will contact you within 24–48 working hours.
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handleRequest} disabled={loading}
              style={{ padding:'7px 16px', background:'#6B1C2A', color:'#fff', border:'none', borderRadius:7, fontSize:12, fontWeight:600, cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1 }}>
              {loading ? 'Submitting...' : 'Yes, Confirm'}
            </button>
            <button onClick={() => setStep('idle')}
              style={{ padding:'7px 16px', background:'transparent', color:'#888', border:'1px solid #e0d8d5', borderRadius:7, fontSize:12, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </>
      )}

      {step === 'done' && (
        <div style={{ fontSize:13, color:'#166534', lineHeight:1.6 }}>
          ✓ Thank you. We have received your request to increase your collateral pledge. Our team will reach out to you within 24–48 working hours to assist with the next steps.
        </div>
      )}

      {step === 'error' && (
        <div style={{ fontSize:13, color:'#c0392b', lineHeight:1.5 }}>
          Something went wrong. Please try again or contact your portfolio manager.
          <button onClick={() => setStep('idle')} style={{ display:'block', marginTop:8, background:'none', border:'none', color:'#6B1C2A', cursor:'pointer', fontSize:12, textDecoration:'underline' }}>Try again</button>
        </div>
      )}
    </div>
  );
}


function ReportRequestCard({ isMobile }) {
  const [step,     setStep]     = useState('idle');
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState({ holding: false, ledger: false });

  const toggle = (type) => setSelected(prev => ({ ...prev, [type]: !prev[type] }));
  const hasSelection = selected.holding || selected.ledger;
  const selectedLabel = [selected.holding && 'Holding Statement', selected.ledger && 'Ledger Statement'].filter(Boolean).join(' & ');

  const handleRequest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/me/request-statement', { types: selectedLabel }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStep('done');
    } catch {
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding: isMobile ? '14px' : '12px 14px', marginBottom: isMobile ? 10 : 0 }}>
      <div style={{ fontSize:10, color:'#888', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>
        Statement Request
      </div>

      {step === 'idle' && (
        <>
          <div style={{ fontSize:12, color:'#555', marginBottom:10, lineHeight:1.5 }}>
            Request your account statements. Select one or both:
          </div>
          <div style={{ marginBottom:12 }}>
            {[['holding', 'Holding Statement'], ['ledger', 'Ledger Statement']].map(([key, label]) => (
              <label key={key} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, cursor:'pointer', fontSize:13, color:'#333' }}>
                <input type="checkbox" checked={selected[key]} onChange={() => toggle(key)}
                  style={{ width:15, height:15, accentColor:'#6B1C2A', cursor:'pointer' }} />
                {label}
              </label>
            ))}
          </div>
          <button onClick={() => hasSelection && setStep('confirm')} disabled={!hasSelection}
            style={{ padding:'7px 16px', background: hasSelection ? '#6B1C2A' : '#ccc', color:'#fff', border:'none', borderRadius:7, fontSize:12, fontWeight:600, cursor: hasSelection ? 'pointer' : 'not-allowed', opacity: hasSelection ? 1 : 0.7 }}>
            Request Statement
          </button>
        </>
      )}

      {step === 'confirm' && (
        <>
          <div style={{ fontSize:13, color:'#333', marginBottom:12, lineHeight:1.5 }}>
            Requesting: <strong>{selectedLabel}</strong>. Our team will contact you within 24–48 working hours.
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handleRequest} disabled={loading}
              style={{ padding:'7px 16px', background:'#6B1C2A', color:'#fff', border:'none', borderRadius:7, fontSize:12, fontWeight:600, cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1 }}>
              {loading ? 'Submitting...' : 'Yes, Confirm'}
            </button>
            <button onClick={() => setStep('idle')}
              style={{ padding:'7px 16px', background:'transparent', color:'#888', border:'1px solid #e0d8d5', borderRadius:7, fontSize:12, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </>
      )}

      {step === 'done' && (
        <div style={{ fontSize:13, color:'#166534', lineHeight:1.6 }}>
          ✓ Thank you. We have received your statement request. Our team will reach out to you within 24–48 working hours to assist with the next steps.
        </div>
      )}

      {step === 'error' && (
        <div style={{ fontSize:13, color:'#c0392b', lineHeight:1.5 }}>
          Something went wrong. Please try again or contact your portfolio manager.
          <button onClick={() => setStep('idle')} style={{ display:'block', marginTop:8, background:'none', border:'none', color:'#6B1C2A', cursor:'pointer', fontSize:12, textDecoration:'underline' }}>Try again</button>
        </div>
      )}
    </div>
  );
}

export default function SummaryCards({ summary, isMobile }) {
  if (!summary) return null;

  const portfolioValue       = summary.portfolio_value       || 0;
  const collateralValue      = summary.collateral_value      || 0;
  const profitSinceInception = summary.profit_since_inception || 0;
  const collateralAvailable  = summary.collateral_available  || 0;
  const xirr                 = summary.xirr                  || 0;
  const winningDays          = summary.winning_days          || 0;
  const totalTradedDays      = summary.total_traded_days     || 0;
  const colPct = portfolioValue > 0 ? Math.round((collateralValue / portfolioValue) * 100) : 0;

  if (isMobile) {
    return (
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:10, fontWeight:600, color:'#888', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Portfolio Summary</div>
        <div style={{ background:'linear-gradient(135deg, #6B1C2A 0%, #8B2439 100%)', borderRadius:12, padding:'16px', marginBottom:10, color:'#fff' }}>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.7)', marginBottom:4 }}>Portfolio Value</div>
          <div style={{ fontSize:24, fontWeight:700 }}>{fmt(portfolioValue)}</div>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.8)', marginTop:4 }}>Collateral: {fmt(collateralValue)} ({colPct}%)</div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
          <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:10, color:'#888', marginBottom:4 }}>Pledge Return</div>
            <div style={{ fontSize:16, fontWeight:700, color: profitSinceInception >= 0 ? '#166534' : '#c0392b' }}>{fmt(profitSinceInception)}</div>
          </div>
          <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:10, color:'#888', marginBottom:4 }}>Est. XIRR</div>
            <div style={{ fontSize:16, fontWeight:700, color:'#6B1C2A' }}>{xirr ? `${xirr.toFixed(2)}%` : '—'}</div>
          </div>
        </div>
        <MarginRequestCard collateralAvailable={collateralAvailable} isMobile={true} />
        <ReportRequestCard isMobile={true} />
      </div>
    );
  }

  // Desktop
  return (
    <div>
      {/* Row 1: Portfolio Value, Collateral Value, Winning Days, Utilisation */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:12 }}>
        <div style={{ background:'linear-gradient(135deg, #6B1C2A 0%, #8B2439 100%)', borderRadius:10, padding:'12px 14px', color:'#fff' }}>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.7)', marginBottom:6 }}>Portfolio Value</div>
          <div style={{ fontSize:20, fontWeight:700 }}>{fmt(portfolioValue)}</div>
        </div>
        <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>Collateral Value</div>
          <div style={{ fontSize:20, fontWeight:700, color:'#333' }}>{fmt(collateralValue)}</div>
          <div style={{ fontSize:10, color:'#aaa', marginTop:2 }}>{colPct}% of portfolio</div>
        </div>
        <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>Winning Days</div>
          <div style={{ fontSize:20, fontWeight:700, color:'#333' }}>{winningDays} / {totalTradedDays}</div>
        </div>
        <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>Utilisation %</div>
          <div style={{ fontSize:20, fontWeight:700, color:'#333' }}>95%</div>
        </div>
      </div>
      {/* Row 2: Pledge Return, Est. XIRR, Collateral Increase Request, Statement Request */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:16 }}>
        <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>Pledge Return</div>
          <div style={{ fontSize:28, fontWeight:700, color: profitSinceInception >= 0 ? '#166534' : '#c0392b' }}>{fmt(profitSinceInception)}</div>
        </div>
        <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>Est. XIRR</div>
          <div style={{ fontSize:28, fontWeight:700, color:'#6B1C2A' }}>{xirr ? `${xirr.toFixed(2)}%` : '—'}</div>
        </div>
        <MarginRequestCard collateralAvailable={collateralAvailable} isMobile={false} />
        <ReportRequestCard isMobile={false} />
      </div>
    </div>
  );
}